import type { TemplateDef, TemplateId } from './types';

/**
 * Template catalogue.
 *
 * The composer's picker shows whichever templates support the current
 * project's `kind`. Each template's render component lives next to
 * the metadata under `templates/`. Adding a new template = appending
 * an entry here + writing the render component + adding any new
 * `layoutVariants` it understands.
 */

export const TEMPLATES: ReadonlyArray<TemplateDef> = [
  {
    id: 'sticker-cam',
    name: 'Sticker Cam',
    blurb:
      'Camera fills the screen and your model triggers emoji bursts when it sees what you trained.',
    supportedKinds: ['image', 'pose'],
    layoutVariants: ['Full-bleed', 'Camera + side panel'],
    defaults: {
      title: 'Sticker Cam',
      subtitle: 'Show me what I learned!',
      actionLabel: 'Start',
    },
  },
  // Sound Reactor declared before Class Quiz so it stays the default
  // audio template — `templatesForKind('audio')` returns templates in
  // declaration order, and the composer seeds with the first match.
  {
    id: 'sound-reactor',
    name: 'Sound Reactor',
    blurb:
      'Make a noise the model knows and watch the screen react. Great for music, claps, or your secret call sign.',
    supportedKinds: ['audio'],
    layoutVariants: ['Pulse', 'Wave bars'],
    defaults: {
      title: 'Sound Reactor',
      subtitle: 'Make some noise.',
      actionLabel: 'Listen',
    },
  },
  {
    id: 'class-quiz',
    name: 'Class Quiz',
    blurb: 'I show you a target. You mimic it. Score points each time the model agrees.',
    supportedKinds: ['image', 'pose', 'audio'],
    layoutVariants: ['Card stack', 'Side-by-side'],
    defaults: {
      title: 'Class Quiz',
      subtitle: 'Can you fool the model — or match it?',
      actionLabel: 'New round',
    },
  },
];

export function getTemplate(id: TemplateId): TemplateDef {
  return TEMPLATES.find((t) => t.id === id) ?? TEMPLATES[0]!;
}

/** Return the templates whose `supportedKinds` include `kind`. */
export function templatesForKind(kind: 'image' | 'audio' | 'pose'): ReadonlyArray<TemplateDef> {
  return TEMPLATES.filter((t) => t.supportedKinds.includes(kind));
}
